<template>
	<div>
		<CommonPageheader :propData="breadcrumbData" />
		<v-container fluid>
			<!-- En-tête -->
			<v-card class="mb-2 elevation-1" outlined style="border-radius: 3px; background-color: #fdfdfd;">
				<v-card-title class="text-h5 font-weight-bold d-flex justify-space-between align-center flex-wrap ga-2">
					<div class="d-flex align-center">
						<v-icon left color="primary">mdi-gavel</v-icon>
						{{ isAdmin ? 'Délibérations' : 'Mes Délibérations' }}
					</div>
					<CommonHistoriqueToggle v-model="historiqueEnabled" />
				</v-card-title>
				<v-card-subtitle>
					{{ isAdmin 
						? 'Gérez toutes les délibérations du système' 
						: 'Délibérations auxquelles vous êtes associé en tant que responsable' 
					}}
				</v-card-subtitle>
				<v-divider class="my-2" />
				<CommonHistoriqueBanner :enabled="historiqueEnabled" class="mx-4 mb-2" />
			</v-card>

			<!-- Filtres -->
			<v-card class="mb-2 elevation-1" outlined style="border-radius: 3px; background-color: #fdfdfd;">
				<v-card-text class="pa-3">
					<v-row dense>
						<v-col cols="12" sm="6">
							<v-text-field
								v-model="deliberationFilters.search"
								label="Rechercher"
								prepend-inner-icon="mdi-magnify"
								variant="outlined"
								density="compact"
								clearable
							/>
						</v-col>
						<v-col cols="12" sm="6">
							<v-select
								v-model="deliberationFilters.etat"
								:items="[
									{ title: 'Tous', value: null },
									{ title: 'En cours', value: 'en_cours' },
									{ title: 'Phase 1 terminée', value: 'phase_1_terminee' },
									{ title: 'Phase 2 terminée', value: 'phase_2_terminee' },
									{ title: 'Validée', value: 'valide' },
									{ title: 'Annulée', value: 'annule' }
								]"
								label="État"
								variant="outlined"
								density="compact"
							/>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<!-- Actions -->
			<v-row dense class="mb-2">
				<v-col cols="12" class="d-flex flex-wrap justify-space-between align-center gap-2">
					<div class="d-flex flex-wrap gap-2">
						<v-btn
							v-if="isAdmin"
							class="mydit-btn"
							size="small"
							@click="openCreateDeliberationDialog"
						>
							<v-icon left color="white">mdi-plus</v-icon>
							Nouvelle délibération
						</v-btn>
						<v-btn
							v-if="isAdmin"
							color="secondary"
							variant="outlined"
							size="small"
							@click="openConditionDialog"
						>
							<v-icon left>mdi-cog</v-icon>
							GÉRER LES CONDITIONS DE DÉLIBÉRATION
						</v-btn>
					</div>
				</v-col>
			</v-row>

			<!-- Loading -->
			<v-card v-if="loadingDeliberations" class="mb-4">
				<v-card-text class="text-center py-8">
					<v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
					<div class="text-body-1">Chargement des délibérations...</div>
				</v-card-text>
			</v-card>

			<!-- Sessions en cours (pas d'année : trop de clics) -->
			<v-card v-else-if="countSessionsInNiveauTree(treeActives) > 0" class="mb-4">
				<v-card-title class="d-flex align-center justify-space-between">
					<span>En cours</span>
					<v-chip size="small" variant="tonal" color="primary">
						{{ countSessionsInNiveauTree(treeActives) }}
					</v-chip>
				</v-card-title>
				<v-card-text>
					<v-expansion-panels v-model="expandedNiveauxActifs" multiple>
						<v-expansion-panel
							v-for="niveauGroup in treeActives"
							:key="`active-niveau-${niveauGroup.niveau}`"
							:value="niveauGroup.niveau"
						>
							<v-expansion-panel-title>
								<div class="d-flex align-center gap-2">
									<v-icon color="primary" size="20">{{ getNiveauIcon(niveauGroup.niveau) }}</v-icon>
									<span class="font-weight-medium">{{ niveauGroup.niveau }}</span>
									<v-chip size="x-small" variant="tonal" color="primary">
										{{ countSessionsInNiveau(niveauGroup) }}
									</v-chip>
								</div>
							</v-expansion-panel-title>
							<v-expansion-panel-text>
								<v-expansion-panels v-model="expandedFilieresActives[niveauGroup.niveau]" multiple>
									<v-expansion-panel
										v-for="filiereGroup in niveauGroup.filieres"
										:key="`active-filiere-${niveauGroup.niveau}-${filiereGroup.key}`"
										:value="filiereGroup.key"
									>
										<v-expansion-panel-title>
											<div class="d-flex align-center gap-2">
												<v-icon size="18" color="secondary">mdi-source-branch</v-icon>
												<span>{{ filiereGroup.filiereLabel }}</span>
												<v-chip size="x-small" variant="tonal">
													{{ filiereGroup.sessions.length }}
												</v-chip>
											</div>
										</v-expansion-panel-title>
										<v-expansion-panel-text>
											<v-expansion-panels>
												<template
													v-for="node in filiereGroup.sessions"
													:key="`active-session-${node.session.id}`"
												>
													<v-expansion-panel>
														<v-expansion-panel-title>
															<div class="w-100 pr-2 session-panel-title">
																<div class="d-flex flex-wrap align-center justify-space-between gap-2">
																	<div class="d-flex align-center gap-2 min-width-0">
																		<v-icon
																			:color="getSessionCategoryColor(node.category)"
																			size="20"
																		>
																			{{ getSessionCategoryIcon(node.category) }}
																		</v-icon>
																		<div class="d-flex flex-column min-width-0">
																			<strong class="text-truncate">{{ node.session.nom || node.session.code }}</strong>
																			<span
																				v-if="node.meta.classeLabel"
																				class="text-caption text-medium-emphasis text-truncate"
																			>
																				{{ node.meta.classeLabel }}
																			</span>
																		</div>
																	</div>
																	<v-chip
																		:color="getSessionCategoryColor(node.category)"
																		size="small"
																		variant="tonal"
																		class="flex-shrink-0"
																	>
																		{{ getSessionCategoryLabel(node.category) }}
																	</v-chip>
																</div>
																<DeliberationSessionSummary
																	v-if="canViewSessionSummary(node.session)"
																	class="mt-1"
																	:deliberations="deliberationsBySession[node.session.id] || []"
																/>
															</div>
														</v-expansion-panel-title>
														<v-expansion-panel-text>
															<DeliberationSessionContent
																:session="node.session"
																:deliberations="deliberationsBySession[node.session.id] || []"
																@view="handleViewDeliberation"
																@edit="handleEditDeliberation"
																@reactivate="handleReactivateDeliberation"
																@view-rattrapage="handleViewRattrapage"
																@download-documents="handleDownloadDocuments"
																@download-resultats-classe="handleDownloadResultatsClasse"
															/>
														</v-expansion-panel-text>
													</v-expansion-panel>
												</template>
											</v-expansion-panels>
										</v-expansion-panel-text>
									</v-expansion-panel>
								</v-expansion-panels>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
				</v-card-text>
			</v-card>

			<!-- Aucune délibération -->
			<v-alert v-else-if="deliberations.length === 0" type="info" variant="tonal" class="mb-4">
				{{ (isAdmin || isPedagogicalManager) 
					? 'Aucune délibération trouvée' 
					: 'Aucune délibération ne vous est assignée' 
				}}
			</v-alert>

			<!-- Sessions terminées (archives : groupées par année) -->
			<v-card v-if="!loadingDeliberations && countSessionsInAnneeFiliereTree(treeTerminees) > 0" class="mb-4">
				<v-card-title class="d-flex align-center justify-space-between">
					<span>Sessions terminées</span>
					<v-chip size="small" variant="tonal" color="success">
						{{ countSessionsInAnneeFiliereTree(treeTerminees) }}
					</v-chip>
				</v-card-title>
				<v-card-text>
					<v-expansion-panels v-model="expandedAnneesTerminees" multiple>
						<v-expansion-panel
							v-for="anneeGroup in treeTerminees"
							:key="`terminee-annee-${anneeGroup.annee}`"
							:value="anneeGroup.annee"
						>
							<v-expansion-panel-title>
								<div class="d-flex align-center gap-2">
									<v-icon color="success" size="20">mdi-calendar-check</v-icon>
									<span class="font-weight-medium">{{ anneeGroup.annee }}</span>
									<v-chip size="x-small" variant="tonal" color="success">
										{{ anneeGroup.filieres.reduce((s, f) => s + f.sessions.length, 0) }}
									</v-chip>
								</div>
							</v-expansion-panel-title>
							<v-expansion-panel-text>
								<v-expansion-panels multiple>
									<v-expansion-panel
										v-for="filiereGroup in anneeGroup.filieres"
										:key="`terminee-filiere-${anneeGroup.annee}-${filiereGroup.key}`"
									>
										<v-expansion-panel-title>
											<div class="d-flex align-center gap-2">
												<v-icon size="18" color="secondary">mdi-source-branch</v-icon>
												<span>{{ filiereGroup.filiereLabel }}</span>
												<v-chip size="x-small" variant="tonal">
													{{ filiereGroup.sessions.length }}
												</v-chip>
											</div>
										</v-expansion-panel-title>
										<v-expansion-panel-text>
											<v-expansion-panels>
												<template
													v-for="node in filiereGroup.sessions"
													:key="`terminee-session-${node.session.id}`"
												>
													<v-expansion-panel>
														<v-expansion-panel-title>
															<div class="w-100 pr-2 session-panel-title">
																<div class="d-flex flex-wrap align-center justify-space-between gap-2">
																	<div class="d-flex align-center gap-2 min-width-0">
																		<v-icon color="success" size="20">mdi-check-circle</v-icon>
																		<div class="d-flex flex-column min-width-0">
																			<strong class="text-truncate">{{ node.session.nom || node.session.code }}</strong>
																			<span
																				v-if="node.meta.classeLabel"
																				class="text-caption text-medium-emphasis text-truncate"
																			>
																				{{ node.meta.classeLabel }}
																			</span>
																		</div>
																	</div>
																	<v-chip color="success" size="small" variant="tonal" class="flex-shrink-0">
																		Terminée
																	</v-chip>
																</div>
																<DeliberationSessionSummary
																	v-if="canViewSessionSummary(node.session)"
																	class="mt-1"
																	:deliberations="deliberationsBySession[node.session.id] || []"
																/>
															</div>
														</v-expansion-panel-title>
														<v-expansion-panel-text>
															<DeliberationSessionContent
																:session="node.session"
																:deliberations="deliberationsBySession[node.session.id] || []"
																@view="handleViewDeliberation"
																@edit="handleEditDeliberation"
																@reactivate="handleReactivateDeliberation"
																@view-rattrapage="handleViewRattrapage"
																@download-documents="handleDownloadDocuments"
																@download-resultats-classe="handleDownloadResultatsClasse"
															/>
														</v-expansion-panel-text>
													</v-expansion-panel>
												</template>
											</v-expansion-panels>
										</v-expansion-panel-text>
									</v-expansion-panel>
								</v-expansion-panels>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
				</v-card-text>
			</v-card>

			<!-- MODALE Liste des rattrapages -->
			<v-dialog v-model="rattrapageDialog.open" max-width="900px" persistent>
				<v-card>
					<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
						<span class="text-h6 modal-header-primary-text">
							Liste des rattrapages
							<span v-if="rattrapageDialog.deliberation">
								- {{ rattrapageDialog.deliberation.session?.nom || rattrapageDialog.deliberation.session?.code }}
								/ {{ rattrapageDialog.deliberation.specialite?.intitule }}
								/ {{ rattrapageDialog.deliberation.semestre?.intitule }}
							</span>
						</span>
						<v-btn icon variant="text" @click="closeRattrapageDialog" class="modal-header-primary-icon">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<div v-if="rattrapageDialog.loading" class="text-center pa-8">
							<v-progress-circular indeterminate color="primary" size="64" />
							<div class="mt-4">Chargement de la liste des rattrapages...</div>
						</div>
						<div v-else-if="!rattrapageDialog.error && rattrapageDialog.items.length === 0" class="text-center pa-8">
							<v-icon size="64" color="success">mdi-check-circle</v-icon>
							<div class="mt-4 text-grey">Aucun étudiant concerné par le rattrapage</div>
						</div>
						<div v-else-if="rattrapageDialog.error" class="text-center pa-8">
							<v-icon size="48" color="error">mdi-alert-circle</v-icon>
							<div class="mt-4 text-error">{{ rattrapageDialog.error }}</div>
						</div>
						<div v-else>
							<v-expansion-panels>
								<v-expansion-panel
									v-for="ec in rattrapageDialog.items"
									:key="ec.ec_id"
								>
									<v-expansion-panel-title>
										<div class="d-flex justify-space-between align-center w-100 pr-4">
											<div>
												<strong>{{ ec.ec_code }}</strong> - {{ ec.ec_intitule }}
												<div class="text-caption text-grey">
													UE: {{ ec.ue_code }} - {{ ec.ue_intitule }}
												</div>
											</div>
											<v-chip size="small" color="warning" variant="tonal">
												{{ ec.nombre_etudiants }} étudiant(s)
											</v-chip>
										</div>
									</v-expansion-panel-title>
									<v-expansion-panel-text>
										<v-list>
											<v-list-item
												v-for="etudiant in ec.etudiants"
												:key="etudiant.inscription_id"
											>
												<v-list-item-title>
													{{ (etudiant.matricule && etudiant.matricule.trim() !== '' ? etudiant.matricule : `#${etudiant.inscription_id}`) + ' - ' + (etudiant.nom_complet && etudiant.nom_complet.trim() !== '' ? etudiant.nom_complet : 'Nom non disponible') }}
												</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-expansion-panel-text>
								</v-expansion-panel>
							</v-expansion-panels>
						</div>
					</v-card-text>
				</v-card>
			</v-dialog>

			<!-- MODALE Créer Délibération -->
			<v-dialog v-model="createDeliberationDialog" max-width="900px" persistent>
				<v-card>
					<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
						<span class="text-h6 modal-header-primary-text">Créer une Délibération et Prédélibération</span>
						<v-btn icon variant="text" @click="closeCreateDeliberationDialog" class="modal-header-primary-icon">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-alert
							v-if="createSessionClosed && createArchiveHint"
							type="warning"
							variant="tonal"
							density="compact"
							class="mb-3"
						>
							{{ createArchiveHint }}
						</v-alert>
						<v-form ref="createFormRef" v-model="createFormValid">
							<v-row>
								<v-col cols="12" md="6">
									<template v-if="loadingSessions">
										<v-skeleton-loader type="list-item-two-line" height="56" width="100%" class="mb-2" />
									</template>
									<template v-else>
										<v-autocomplete
											v-model="createForm.session_id"
											:items="sessionsForSelect"
											item-title="title"
											item-value="value"
											label="Session *"
											variant="outlined"
											density="compact"
											:rules="[v => !!v || 'La session est requise']"
											clearable
											placeholder="Rechercher une session..."
											prepend-inner-icon="mdi-magnify"
											@update:model-value="onSessionChange"
										>
											<template v-slot:item="{ item, props }">
												<v-list-item v-bind="props">
													<template #title>
														<div class="d-flex justify-space-between align-center">
															<span>{{ item.raw.title }}</span>
															<div class="d-flex align-center ga-1">
																<v-chip
																	v-if="item.raw.cloturee"
																	size="x-small"
																	color="primary"
																	variant="flat"
																>
																	Clôturée
																</v-chip>
																<v-chip
																	:color="item.raw.etatColor"
																	size="x-small"
																	variant="tonal"
																>
																	{{ item.raw.etat }}
																</v-chip>
															</div>
														</div>
													</template>
												</v-list-item>
											</template>
										</v-autocomplete>
									</template>
								</v-col>
								<v-col cols="12" md="6">
									<template v-if="loadingSpecialites || (createForm.session_id && loadingMaquettes)">
										<v-skeleton-loader type="list-item-two-line" height="56" width="100%" class="mb-2" />
									</template>
									<template v-else>
										<v-autocomplete
											v-model="createForm.specialite_id"
											:items="specialitesForSelect"
											item-title="title"
											item-value="value"
											label="Spécialité *"
											variant="outlined"
											density="compact"
											:rules="[v => !!v || 'La spécialité est requise']"
											:disabled="!createForm.session_id"
											:no-data-text="createForm.session_id ? 'Aucune spécialité disponible' : 'Sélectionnez une session d\'abord'"
											clearable
											placeholder="Rechercher une spécialité..."
											prepend-inner-icon="mdi-magnify"
										/>
									</template>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12" md="6">
									<template v-if="loadingSemestres || (createForm.session_id && createForm.specialite_id && loadingMaquettes)">
										<v-skeleton-loader type="list-item-two-line" height="56" width="100%" class="mb-2" />
									</template>
									<template v-else>
										<v-autocomplete
											v-model="createForm.semestre_id"
											:items="semestresForSelect"
											item-title="title"
											item-value="value"
											label="Semestre *"
											variant="outlined"
											density="compact"
											:rules="[v => !!v || 'Le semestre est requis']"
											:disabled="!createForm.session_id || !createForm.specialite_id"
											:no-data-text="createForm.session_id && createForm.specialite_id ? 'Aucun semestre disponible' : 'Sélectionnez une session et une spécialité d\'abord'"
											clearable
											placeholder="Rechercher un semestre..."
											prepend-inner-icon="mdi-magnify"
										/>
									</template>
								</v-col>
								<v-col cols="12" md="6">
									<v-text-field
										model-value="Première délibération"
										label="Type de délibération"
										variant="outlined"
										density="compact"
										readonly
										hint="La seconde délibération (rattrapage) se crée depuis une première délibération validée."
										persistent-hint
									/>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12" md="6">
									<template v-if="loadingConditions">
										<v-skeleton-loader type="list-item-two-line" height="56" width="100%" class="mb-2" />
									</template>
									<template v-else>
										<v-autocomplete
											v-model="createForm.deliberation_condition_id"
											:items="conditionsForSelect"
											item-title="title"
											item-value="value"
											label="Condition de délibération *"
											variant="outlined"
											density="compact"
											:rules="[v => !!v || 'La condition est requise']"
											clearable
											placeholder="Rechercher une condition..."
											prepend-inner-icon="mdi-magnify"
										/>
									</template>
								</v-col>
								<v-col cols="12" md="6">
									<template v-if="loadingPersonnels || (createForm.session_id && loadingMaquettes)">
										<v-skeleton-loader type="list-item-two-line" height="56" width="100%" class="mb-2" />
									</template>
									<template v-else>
										<v-autocomplete
											v-model="createForm.responsable_id"
											:items="professeursForSelect"
											item-title="title"
											item-value="value"
											label="Professeur responsable (président) *"
											variant="outlined"
											density="compact"
											:rules="[v => !!v || 'Le responsable est requis']"
											:no-data-text="loadingPersonnels ? 'Chargement...' : 'Aucun professeur disponible'"
											clearable
											placeholder="Rechercher un professeur..."
											prepend-inner-icon="mdi-magnify"
										/>
									</template>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12" md="6">
									<template v-if="loadingPersonnels || (createForm.session_id && loadingSessions)">
										<v-skeleton-loader type="list-item-two-line" height="56" width="100%" class="mb-2" />
									</template>
									<template v-else>
										<v-autocomplete
											v-model="createForm.predeliberation_responsable_id"
											:items="responsablesForSelect"
											item-title="title"
											item-value="value"
											label="Responsable pédagogique *"
											variant="outlined"
											density="compact"
											:rules="[v => !!v || 'Le responsable pédagogique est requis']"
											:disabled="!createForm.session_id"
											:no-data-text="createForm.session_id ? 'Aucun responsable pédagogique disponible' : 'Sélectionnez une session d\'abord'"
											clearable
											placeholder="Rechercher un responsable..."
											prepend-inner-icon="mdi-magnify"
										/>
									</template>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12">
									<v-textarea
										v-model="createForm.commentaire"
										label="Commentaire (délibération)"
										variant="outlined"
										density="compact"
										rows="2"
									/>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12">
									<v-textarea
										v-model="createForm.predeliberation_commentaire"
										label="Commentaire (prédélibération)"
										variant="outlined"
										density="compact"
										rows="2"
									/>
								</v-col>
							</v-row>
						</v-form>
					</v-card-text>
					<v-card-actions style="display: flex; justify-content: center">
						<v-btn size="small" variant="outlined" color="secondary" @click="closeCreateDeliberationDialog">Annuler</v-btn>
						<v-btn size="small" class="mydit-btn" @click="createDeliberation" :loading="saving" :disabled="!createFormValid || !canMutateCreateSession">
							<v-icon left color="white">mdi-content-save</v-icon>
							Créer
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- MODALE Gestion Conditions (comme infrastructure avec types d'équipement) -->
			<v-dialog v-model="conditionDialogOpen" max-width="1000px" persistent>
				<v-card>
					<v-card-title class="text-h6 text-center d-flex justify-space-between align-center modal-header-primary">
						<span class="modal-header-primary-text">Gestion des conditions de délibération</span>
						<v-btn icon variant="text" @click="closeConditionDialog" class="modal-header-primary-icon">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<!-- Formulaire dans une carte séparée -->
						<v-card outlined class="mb-4">
							<v-card-text>
								<v-form ref="conditionFormRef" v-model="conditionFormValid" @submit.prevent="saveCondition">
									<v-row class="mb-2" justify="center">
										<v-col cols="12" md="6">
											<v-text-field
												v-model="conditionForm.intitule"
												label="Intitulé"
												required
												variant="outlined"
												density="compact"
												:rules="[v => !!v || 'L\'intitulé est requis']"
											/>
										</v-col>
										<v-col cols="12" md="6">
											<v-text-field
												v-model="conditionForm.code"
												label="Code"
												disabled
												variant="outlined"
												density="compact"
												hint="Le code est généré automatiquement"
												persistent-hint
											/>
										</v-col>
									</v-row>
									<v-row class="mb-2" justify="center">
										<v-col cols="12">
											<v-text-field
												v-model="conditionForm.description"
												label="Description"
												variant="outlined"
												density="compact"
											/>
										</v-col>
									</v-row>
									<v-row class="mb-2" justify="center">
										<v-col cols="12" md="6">
											<v-text-field
												v-model.number="conditionForm.note_min_modifiable"
												label="Note minimale modifiable"
												type="number"
												min="0"
												max="20"
												variant="outlined"
												density="compact"
												:rules="conditionRules.noteMinModifiable"
												hint="Note minimale que vous avez le droit de modifier sur une note d’EC (devoir, examen ou rattrapage)."
												persistent-hint
											/>
										</v-col>
										<v-col cols="12" md="6">
											<v-text-field
												v-model.number="conditionForm.note_max_modifiable"
												label="Note maximale modifiable"
												type="number"
												min="0"
												max="20"
												variant="outlined"
												density="compact"
												:rules="conditionRules.noteMaxModifiable"
												hint="Note maximale que vous avez le droit de modifier sur une note d’EC (devoir, examen ou rattrapage)."
												persistent-hint
											/>
										</v-col>
									</v-row>
									<v-row class="mb-2" justify="center">
										<v-col cols="12" md="6">
											<v-text-field
												v-model.number="conditionForm.points_max_ajoutables"
												label="Points maximum ajoutables"
												type="number"
												min="0"
												max="10"
												variant="outlined"
												density="compact"
												:rules="conditionRules.pointsMaxAjoutables"
												hint="Pour un étudiant: nombre total maximum de points que vous pouvez ajouter au cumul de toutes ses modifications."
												persistent-hint
											/>
										</v-col>
										<v-col cols="12" md="6">
											<v-text-field
												v-model.number="conditionForm.nombre_max_modifications"
												label="Nombre maximum de modifications"
												type="number"
												min="0"
												variant="outlined"
												density="compact"
												:rules="conditionRules.nombreMaxModifications"
												hint="Pour un étudiant: nombre maximum d’EC (notes) que vous pouvez modifier au total pendant la délibération."
												persistent-hint
											/>
										</v-col>
									</v-row>
									<v-row class="mb-2" justify="center">
										<v-col cols="12" md="6">
											<v-text-field
												v-model.number="conditionForm.note_eliminatoire"
												label="Note éliminatoire *"
												type="number"
												min="0"
												max="20"
												variant="outlined"
												density="compact"
												:rules="conditionRules.noteEliminatoire"
												hint="Seuil éliminatoire au niveau UE/EC: si une note d’examen ou une moyenne d’EC est ≤ à ce seuil, l’UE est bloquante et l’étudiant va en Phase 2."
												persistent-hint
											/>
										</v-col>
										<v-col cols="12" md="6">
											<v-text-field
												v-model.number="conditionForm.seuil_credit_requis"
												label="Crédits requis du semestre"
												type="number"
												min="0"
												variant="outlined"
												density="compact"
												:rules="conditionRules.seuilCreditRequis"
												:readonly="!seuilCreditManualEdit"
												:append-inner-icon="seuilCreditManualEdit ? 'mdi-lock-open-variant' : 'mdi-pencil'"
												@click:append-inner="seuilCreditManualEdit = !seuilCreditManualEdit"
												hint="Par défaut: 30 (lecture seule). Cliquez sur le crayon pour modifier manuellement si nécessaire."
												persistent-hint
											/>
										</v-col>
									</v-row>
									<v-row class="mb-4" justify="center">
										<v-col cols="12" md="6">
											<v-switch
												v-model="conditionForm.is_active"
												label="Active"
												color="primary"
											/>
										</v-col>
									</v-row>
									<div class="d-flex justify-center mb-4">
										<v-btn color="primary" class="mx-2" @click="saveCondition" :loading="conditionSaving" :disabled="!conditionFormValid">
											<v-icon left color="white">mdi-content-save</v-icon>
											{{ editingCondition ? 'Modifier' : 'Ajouter' }}
										</v-btn>
										<v-btn v-if="editingCondition" class="mx-2" @click="closeConditionForm">Annuler</v-btn>
									</div>
								</v-form>
							</v-card-text>
						</v-card>
						
						<!-- Liste des conditions dans une carte séparée -->
						<v-card outlined>
							<v-card-title class="text-subtitle-1">Liste des conditions de délibération</v-card-title>
							<v-card-text>
								<v-data-table 
									:headers="conditionHeaders" 
									:items="conditions" 
									:loading="conditionLoading"
									:items-per-page="conditionPagination.per_page"
									:page="conditionPagination.page"
									:server-items-length="conditionPagination.total"
									@update:page="handleConditionPageChange"
									@update:items-per-page="handleConditionPerPageChange"
									class="elevation-1"
									density="compact"
								>
									<template #item.is_active="{ item }">
										<v-chip :color="item.is_active ? 'success' : 'error'" size="small">{{ item.is_active ? 'Actif' : 'Inactif' }}</v-chip>
									</template>
									<template #item.note_min_modifiable="{ item }">
										{{ item.note_min_modifiable !== null ? item.note_min_modifiable : '-' }}
									</template>
									<template #item.note_max_modifiable="{ item }">
										{{ item.note_max_modifiable !== null ? item.note_max_modifiable : '-' }}
									</template>
									<template #item.points_max_ajoutables="{ item }">
										{{ item.points_max_ajoutables !== null ? item.points_max_ajoutables : '-' }}
									</template>
									<template #item.nombre_max_modifications="{ item }">
										{{ item.nombre_max_modifications !== null ? item.nombre_max_modifications : '-' }}
									</template>
									<template #item.seuil_credit_requis="{ item }">
										{{ item.seuil_credit_requis ?? '-' }}
									</template>
									<template #item.actions="{ item }">
										<div class="d-flex justify-center gap-2">
											<v-btn size="x-small" variant="text" icon="mdi-pencil" color="primary" @click="openEditConditionDialog(item)" />
											<v-btn 
												size="x-small" 
												variant="text" 
												:icon="item.is_active ? 'mdi-toggle-switch-off' : 'mdi-toggle-switch'"
												:color="item.is_active ? 'warning' : 'success'"
												@click="toggleConditionActive(item)"
												:loading="conditionTogglingId === item.id"
											/>
											<v-btn 
												size="x-small" 
												variant="text" 
												icon="mdi-delete"
												color="error" 
												@click="confirmDeleteCondition(item)"
												:disabled="item.deliberations && item.deliberations.length > 0"
											/>
										</div>
									</template>
								</v-data-table>
							</v-card-text>
						</v-card>
					</v-card-text>
				</v-card>
			</v-dialog>

			<!-- Dialog de confirmation de suppression Condition -->
			<v-dialog v-model="conditionDeleteDialogOpen" max-width="500px" persistent>
				<v-card>
					<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
						<span class="text-h6 modal-header-primary-text">Confirmer la suppression</span>
						<v-btn icon variant="text" @click="conditionDeleteDialogOpen = false" class="modal-header-primary-icon">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						Êtes-vous sûr de vouloir supprimer la condition "{{ conditionItemToDelete?.intitule }}" ?
					</v-card-text>
					<v-card-actions class="justify-center">
						<v-chip class="ma-2" color="error" variant="outlined" @click="deleteCondition" :loading="conditionDeleting">Supprimer</v-chip>
						<v-chip class="ma-2" color="secondary" variant="outlined" @click="conditionDeleteDialogOpen = false">Annuler</v-chip>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- MODALE Modifier Délibération -->
			<v-dialog v-model="editDeliberationDialog" max-width="800px" persistent>
				<v-card>
					<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
						<span class="text-h6 modal-header-primary-text">Modifier la Délibération</span>
						<v-btn icon variant="text" @click="closeEditDeliberationDialog" class="modal-header-primary-icon">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-form ref="editFormRef" v-model="editFormValid" @submit.prevent="saveEditDeliberation">
							<v-row>
								<v-col cols="12">
									<v-autocomplete
										v-model="editForm.deliberation_condition_id"
										:items="conditionsForSelect"
										item-title="title"
										item-value="value"
										label="Condition de délibération *"
										variant="outlined"
										density="compact"
										:rules="[v => !!v || 'La condition est requise']"
										clearable
										placeholder="Rechercher une condition..."
										prepend-inner-icon="mdi-magnify"
									/>
								</v-col>
								<v-col cols="12">
									<v-autocomplete
										v-model="editForm.responsable_id"
										:items="professeursForSelect"
										item-title="title"
										item-value="value"
										label="Professeur responsable (président) *"
										variant="outlined"
										density="compact"
										:rules="[v => !!v || 'Le responsable est requis']"
										clearable
										placeholder="Rechercher un professeur..."
										prepend-inner-icon="mdi-magnify"
									/>
								</v-col>
								<v-col cols="12">
									<v-textarea
										v-model="editForm.commentaire"
										label="Commentaire"
										variant="outlined"
										density="compact"
										rows="3"
									/>
								</v-col>
							</v-row>
						</v-form>
					</v-card-text>
					<v-card-actions class="d-flex justify-center">
						<v-btn color="primary" @click="saveEditDeliberation" :loading="savingEdit" :disabled="!editFormValid">
							<v-icon left color="white">mdi-content-save</v-icon>
							Enregistrer
						</v-btn>
						<v-btn variant="outlined" @click="closeEditDeliberationDialog">Annuler</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- MODALE Téléchargement résultats de classe -->
			<v-dialog v-model="resultatsClasseDialog.open" max-width="480px" persistent>
				<v-card>
					<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
						<span class="text-h6 modal-header-primary-text">
							{{ impressionDialogTitle }}
							<span v-if="resultatsClasseDialog.specialite">
								— {{ resultatsClasseDialog.specialite.intitule }}
							</span>
						</span>
						<v-btn
							v-if="!resultatsClasseDialog.loading"
							icon
							variant="text"
							class="modal-header-primary-icon"
							@click="closeResultatsClasseDialog"
						>
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<div v-if="resultatsClasseDialog.loading" class="text-center pa-8">
							<v-progress-circular indeterminate color="primary" size="64" />
							<div class="mt-4">Génération du document en cours…</div>
							<div class="text-caption text-medium-emphasis mt-2">
								{{ impressionDialogHint }}
							</div>
						</div>
						<div v-else-if="resultatsClasseDialog.error" class="text-center pa-6">
							<v-icon size="48" color="error">mdi-alert-circle</v-icon>
							<div class="mt-4 text-error">{{ resultatsClasseDialog.error }}</div>
						</div>
					</v-card-text>
					<v-card-actions v-if="!resultatsClasseDialog.loading" class="justify-center pb-4">
						<v-btn variant="outlined" @click="closeResultatsClasseDialog">Fermer</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- MODALE Télécharger Documents -->
			<DeliberationDocumentsModal
				v-model="downloadDocumentsDialog"
				:deliberation="selectedDeliberationForDownload"
				@download="handleDownloadDocumentsConfirm"
			/>

			<!-- Dialog de confirmation de réactivation -->
			<v-dialog v-model="reactivateDeliberationDialog" max-width="500px" persistent>
				<v-card>
					<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
						<span class="text-h6 modal-header-primary-text d-flex align-center">
							<v-icon color="white" class="mr-2">mdi-restore</v-icon>
							Confirmer la réactivation
						</span>
						<v-btn icon variant="text" @click="reactivateDeliberationDialog = false" class="modal-header-primary-icon">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-alert type="info" variant="tonal" class="mb-4">
							<strong>Information</strong>
						</v-alert>
						<p class="mb-2">Êtes-vous sûr de vouloir réactiver cette délibération ?</p>
						<p class="text-body-2 text-grey">
							Elle repassera à l'état "En cours" et la prédélibération associée sera également réactivée si elle est annulée.
						</p>
					</v-card-text>
					<v-card-actions class="justify-center">
						<v-btn variant="outlined" @click="reactivateDeliberationDialog = false">Annuler</v-btn>
						<v-btn color="success" @click="doReactivateDeliberation" :loading="loading">
							<v-icon left>mdi-restore</v-icon>
							Confirmer la réactivation
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-container>
	</div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import type { Deliberation, DeliberationFilters, DeliberationCondition, DeliberationConditionRequest, DeliberationRequest } from '@/types/deliberation'

// Type pour le formulaire qui accepte null
type DeliberationFormData = {
	session_id: number | null
	specialite_id: number | null
	semestre_id: number | null
	type_deliberation: 'premiere' | 'seconde'
	deliberation_condition_id: number | null
	responsable_id: number | null
	predeliberation_responsable_id: number | null
	commentaire?: string
	predeliberation_commentaire?: string
}
import { useDeliberation } from '@/composables/useDeliberation'
import { useSession } from '@/composables/useSession'
import { useSpecialite } from '@/composables/useSpecialite'
import { useSemestres } from '@/composables/useSemestres'
import { usePersonnelList } from '@/composables/usePersonnelList'
import { usePermissions } from '@/composables/usePermissions'
import { useProfileStore } from '@/stores/profile'
import { canViewDeliberationSessionSummary } from '@/utils/deliberationAccessHelpers'
import { useMaquettes } from '@/composables/useMaquettes'
import { useApi } from '@/composables/useApi'
import { useSessionUser } from '@/composables/useSessionUser'
import { useFiliere } from '@/composables/useFiliere'
import DeliberationSessionContent from '@/components/deliberation/DeliberationSessionContent.vue'
import DeliberationDocumentsModal from '@/components/deliberation/DeliberationDocumentsModal.vue'
import { toast } from 'vue3-toastify'
import { loadRattrapageOutcomeItems } from '@/utils/deliberationRattrapageHelpers'
import { buildMapByLaravelInscriptionId } from '@/utils/inscriptionIdHelpers'
import { fetchAllEtudiantsBisForSessionSpecialite, warnEtudiantsBisTruncated } from '@/utils/etudiantsBisPagination'
import { getSessionEtat, getSessionEtatColor, sortSessionsByEtat } from '@/utils/sessionHelpers'
import {
	buildDeliberationNiveauTree,
	buildDeliberationAnneeFiliereTree,
	countSessionsInNiveauTree,
	countSessionsInAnneeFiliereTree,
	getSessionCategoryLabel,
	getSessionCategoryColor,
	mergeSessionRelations,
	type DeliberationCategoryScope,
	type DeliberationNiveauGroup,
} from '@/utils/deliberationSessionGrouping'
import { useResultatsClasse } from '@/composables/useResultatsClasse'
import { generateResultatsClassePDF } from '@/composables/useResultatsClassePDF'
import { generateResultatsAnnuelsExcel } from '@/composables/useResultatsAnnuelsExcel'
import type { ImpressionResultatsVariant } from '@/types/resultatsClasse'
import { useHistoriqueMode } from '@/composables/useHistoriqueMode'
import { isSessionHistorique, isOrphanSelectId } from '@/utils/historiqueSession'
import { useClosedSessionGuard } from '@/composables/useClosedSessionGuard'
import { isSessionCloturee, canMutateClosedSession } from '@/utils/sessionCloture'

definePageMeta({
	layout: "maindashboard",
	middleware: ['auth']
})

const { isAdmin, isSuperAdmin, isPedagogicalManager } = usePermissions()
const { enabled: historiqueEnabled, contextKey, fetchOptions } = useHistoriqueMode()
const profileStore = useProfileStore()
const canOverrideClosedSession = computed(() =>
	canMutateClosedSession(isAdmin.value, isSuperAdmin.value)
)

const currentPersonnelId = computed(() => {
	const profile = profileStore.profile
	if (!profile) return undefined
	return (profile as any).personnel?.id || (profile as any).personnel_id || (profile as any).id_personnel as number | undefined
})

const summaryAccessFlags = computed(() => ({
	isAdmin: isAdmin.value,
	isSuperAdmin: isSuperAdmin.value,
	isPedagogicalManager: isPedagogicalManager.value,
}))

function canViewSessionSummary(session: any): boolean {
	return canViewDeliberationSessionSummary(
		session,
		currentPersonnelId.value,
		summaryAccessFlags.value,
		deliberationsBySession.value[session.id] || []
	)
}
const { fetchEtudiantsBisPageSilent } = useSessionUser()

const breadcrumbData = ref({
	current: 'Délibération',
	list: ['Accueil', 'Délibération']
})

// ============================================================================
// DÉLIBÉRATIONS
// ============================================================================
const deliberations = ref<Deliberation[]>([])
const loadingDeliberations = ref(false)

// Enrichissement pour groupement (classe / filière) — une seule instance des composables
const { sessions, fetchSessions } = useSession()
const { filieres, fetchFilieres } = useFiliere()

// Filtres pour les délibérations
const deliberationFilters = reactive({
	session_id: null as number | null,
	specialite_id: null as number | null,
	semestre_id: null as number | null,
	etat: null as string | null,
	search: '' as string,
	page: 1,
	per_page: 50
})

const deliberationsBySession = computed(() => {
	const grouped: Record<number, Deliberation[]> = {}
	deliberations.value.forEach(delib => {
		const sessionId = delib.session?.id
		if (!sessionId) return
		if (!grouped[sessionId]) {
			grouped[sessionId] = []
		}
		grouped[sessionId].push(delib)
	})
	return grouped
})

/** Admin / RP : cycle session complet. Prof / responsable assigné : périmètre visible uniquement. */
const deliberationCategoryScope = computed((): DeliberationCategoryScope => {
	if (isAdmin.value || isSuperAdmin.value || isPedagogicalManager.value) {
		return 'session'
	}
	return 'assigned'
})

/**
 * Pour la catégorisation En cours / Terminée en vue assignée :
 * ne juger que les délibs dont l'utilisateur est président (responsable_id).
 * Fallback sur le jeu complet si aucune n'est à son nom (évite d'effacer un dataset déjà filtré autrement).
 */
const deliberationsBySessionForCategory = computed(() => {
	if (deliberationCategoryScope.value === 'session') {
		return deliberationsBySession.value
	}
	const pid = currentPersonnelId.value
	if (pid == null) return deliberationsBySession.value

	const scoped: Record<number, Deliberation[]> = {}
	for (const [sessionIdStr, delibs] of Object.entries(deliberationsBySession.value)) {
		const sessionId = Number(sessionIdStr)
		const mine = delibs.filter(
			d => d.responsable_id != null && Number(d.responsable_id) === Number(pid)
		)
		scoped[sessionId] = mine.length > 0 ? mine : delibs
	}
	return scoped
})

// Sessions enrichies (classe / semestres) pour le groupement
const sessionsEnrichiesMap = ref<Map<number, any>>(new Map())

const sessionsAvecDeliberations = computed(() => {
	const sessionsMap = new Map<number, any>()
	const sourceBySession = deliberationsBySessionForCategory.value
	Object.values(sourceBySession).forEach((delibs) => {
		delibs.forEach(d => {
			if (d.session?.id && !sessionsMap.has(d.session.id)) {
				const embedded = d.session
				const enriched = sessionsEnrichiesMap.value.get(embedded.id)
				sessionsMap.set(embedded.id, mergeSessionRelations(embedded, enriched))
			}
		})
	})
	return Array.from(sessionsMap.values())
})

const treeActives = computed(() =>
	buildDeliberationNiveauTree(
		sessionsAvecDeliberations.value,
		deliberationsBySessionForCategory.value,
		filieres.value,
		['en_cours', 'prevue'],
		deliberationCategoryScope.value
	)
)

const treeTerminees = computed(() =>
	buildDeliberationAnneeFiliereTree(
		sessionsAvecDeliberations.value,
		deliberationsBySessionForCategory.value,
		filieres.value,
		['terminee'],
		deliberationCategoryScope.value
	)
)

const expandedNiveauxActifs = ref<string[]>([])
const expandedFilieresActives = ref<Record<string, string[]>>({})
const expandedAnneesTerminees = ref<string[]>([])

function countSessionsInNiveau(niveauGroup: DeliberationNiveauGroup): number {
	return niveauGroup.filieres.reduce((sum, f) => sum + f.sessions.length, 0)
}

function getNiveauIcon(niveau: string): string {
	switch (niveau) {
		case 'Licence':
			return 'mdi-school'
		case 'Master':
			return 'mdi-school-outline'
		case 'Certification':
			return 'mdi-certificate'
		default:
			return 'mdi-book-education-outline'
	}
}

function getSessionCategoryIcon(category: string): string {
	switch (category) {
		case 'en_cours':
			return 'mdi-clock-outline'
		case 'prevue':
			return 'mdi-calendar-clock'
		case 'terminee':
			return 'mdi-check-circle'
		default:
			return 'mdi-circle-outline'
	}
}

function syncExpandedPanels() {
	const niveaux = treeActives.value.map(n => n.niveau)
	expandedNiveauxActifs.value = [...niveaux]

	const filieresOpen: Record<string, string[]> = {}
	for (const niveauGroup of treeActives.value) {
		filieresOpen[niveauGroup.niveau] = niveauGroup.filieres.map(f => f.key)
	}
	expandedFilieresActives.value = filieresOpen

	// Archives : années repliées par défaut
	expandedAnneesTerminees.value = []
}

async function enrichSessionsForGrouping(delibs: Deliberation[]) {
	const sessionIds = new Set<number>()
	delibs.forEach(d => {
		if (d.session?.id) sessionIds.add(d.session.id)
	})
	if (sessionIds.size === 0) {
		sessionsEnrichiesMap.value = new Map()
		return
	}

	try {
		await Promise.all([
			fetchSessions({ withRelations: true, ...fetchOptions() }).catch(() => undefined),
			fetchFilieres().catch(() => undefined),
		])
		const map = new Map<number, any>()
		for (const s of sessions.value) {
			if (sessionIds.has(s.id)) {
				map.set(s.id, s)
			}
		}
		sessionsEnrichiesMap.value = map
	} catch (error) {
		console.warn('Enrichissement sessions pour groupement:', error)
	}
}

async function loadDeliberations() {
	loadingDeliberations.value = true
	try {
		const filters: DeliberationFilters = {
			page: deliberationFilters.page,
			all: true,
			...fetchOptions(),
		}

		if (deliberationFilters.session_id) filters.session_id = deliberationFilters.session_id
		if (deliberationFilters.specialite_id) filters.specialite_id = deliberationFilters.specialite_id
		if (deliberationFilters.semestre_id) filters.semestre_id = deliberationFilters.semestre_id
		if (deliberationFilters.etat) {
			filters.etat = deliberationFilters.etat as 'en_cours' | 'phase_1_terminee' | 'phase_2_terminee' | 'valide' | 'annule'
		}

		const result = await fetchDeliberations(filters)

		let loaded: Deliberation[] = []
		if (result.success) {
			if (Array.isArray(result.data)) {
				loaded = result.data
			} else if (result.data?.data) {
				loaded = result.data.data
			}
		}
		deliberations.value = loaded
		await enrichSessionsForGrouping(loaded)
		syncExpandedPanels()
	} catch (error) {
		console.error('Erreur chargement délibérations:', error)
		deliberations.value = []
		sessionsEnrichiesMap.value = new Map()
	} finally {
		loadingDeliberations.value = false
	}
}

// ============================================================================
// CONDITIONS (MODALE)
// ============================================================================
const conditions = ref<DeliberationCondition[]>([])
const conditionLoading = ref(false)
const conditionDialogOpen = ref(false)
const conditionPagination = reactive({
	page: 1,
	per_page: 15,
	total: 0
})

const conditionHeaders = [
	{ title: 'Intitulé', key: 'intitule', sortable: true },
	{ title: 'Note min modifiable', key: 'note_min_modifiable', sortable: false },
	{ title: 'Note max modifiable', key: 'note_max_modifiable', sortable: false },
	{ title: 'Points max ajoutables', key: 'points_max_ajoutables', sortable: false },
	{ title: 'Nb max modifications', key: 'nombre_max_modifications', sortable: false },
	{ title: 'Note éliminatoire', key: 'note_eliminatoire', sortable: false },
	{ title: 'Seuil crédit', key: 'seuil_credit_requis', sortable: false },
	{ title: 'Statut', key: 'is_active', sortable: false },
	{ title: 'Actions', key: 'actions', sortable: false, align: 'center' as const }
]

const editingCondition = ref<DeliberationCondition | null>(null)
const conditionForm = reactive<DeliberationConditionRequest>({
	intitule: '',
	code: undefined,
	description: undefined,
	note_min_modifiable: undefined,
	note_max_modifiable: undefined,
	points_max_ajoutables: undefined,
	nombre_max_modifications: undefined,
	note_eliminatoire: undefined,
	seuil_credit_requis: 30,
	is_active: true
})
const seuilCreditManualEdit = ref(false)
const conditionFormValid = ref(false)
const conditionFormRef = ref()
const conditionSaving = ref(false)

function isEmpty(v: unknown): boolean {
	if (v == null || v === '') return true
	if (typeof v === 'string' && v.trim() === '') return true
	return false
}
const conditionRules = {
	noteEliminatoire: [
		(v: unknown) => {
			if (isEmpty(v)) return 'La note éliminatoire est requise'
			const n = Number(v)
			if (Number.isNaN(n)) return 'La note éliminatoire est requise'
			if (n < 0 || n > 20) return 'Entre 0 et 20'
			return true
		}
	],
	pointsMaxAjoutables: [
		(v: unknown) => {
			if (isEmpty(v)) return true
			const n = Number(v)
			if (Number.isNaN(n)) return true
			return (n >= 0 && n <= 10) || 'Entre 0 et 10 (max 10)'
		}
	],
	noteMinModifiable: [
		(v: unknown) => {
			if (isEmpty(v)) return true
			const n = Number(v)
			if (Number.isNaN(n)) return true
			return (n >= 0 && n <= 20) || 'Entre 0 et 20'
		}
	],
	noteMaxModifiable: [
		(v: unknown) => {
			if (isEmpty(v)) return true
			const n = Number(v)
			if (Number.isNaN(n)) return true
			return (n >= 0 && n <= 20) || 'Entre 0 et 20'
		},
		(v: unknown) => {
			const min = conditionForm.note_min_modifiable
			if (isEmpty(v) || min == null || Number.isNaN(Number(v))) return true
			return Number(v) >= Number(min) || 'Doit être ≥ note min'
		}
	],
	nombreMaxModifications: [
		(v: unknown) => isEmpty(v) || Number.isNaN(Number(v)) || Number(v) >= 0 || 'Min 0'
	],
	seuilCreditRequis: [
		(v: unknown) => isEmpty(v) || Number.isNaN(Number(v)) || Number(v) >= 0 || 'Min 0'
	]
}
const conditionTogglingId = ref<number | null>(null)
const conditionDeleteDialogOpen = ref(false)
const conditionItemToDelete = ref<DeliberationCondition | null>(null)
const conditionDeleting = ref(false)

async function loadConditions() {
	conditionLoading.value = true
	try {
		const result = await fetchDeliberationConditions({
			page: conditionPagination.page,
			per_page: conditionPagination.per_page
		})

		if (result.success) {
			if (Array.isArray(result.data)) {
				conditions.value = result.data
				conditionPagination.total = result.data.length
			} else if (result.data?.data) {
				conditions.value = result.data.data
				conditionPagination.total = result.data.total || result.data.data.length
			}
		}
	} finally {
		conditionLoading.value = false
	}
}

function handleConditionPageChange(page: number) {
	conditionPagination.page = page
	loadConditions()
}

function handleConditionPerPageChange(perPage: number) {
	conditionPagination.per_page = perPage
	conditionPagination.page = 1
	loadConditions()
}

function openConditionDialog() {
	conditionDialogOpen.value = true
	loadConditions()
}

function closeConditionDialog() {
	conditionDialogOpen.value = false
	setTimeout(() => {
		editingCondition.value = null
		resetConditionForm()
		conditionFormRef.value?.resetValidation()
	}, 300)
}

function closeConditionForm() {
	editingCondition.value = null
	resetConditionForm()
	conditionFormRef.value?.resetValidation()
}

function openEditConditionDialog(condition: DeliberationCondition) {
	editingCondition.value = condition
	conditionForm.intitule = condition.intitule
	conditionForm.code = condition.code || undefined
	conditionForm.description = condition.description || undefined
	conditionForm.note_min_modifiable = condition.note_min_modifiable ?? undefined
	conditionForm.note_max_modifiable = condition.note_max_modifiable ?? undefined
	conditionForm.points_max_ajoutables = condition.points_max_ajoutables ?? undefined
	conditionForm.nombre_max_modifications = condition.nombre_max_modifications ?? undefined
	conditionForm.note_eliminatoire = condition.note_eliminatoire ?? undefined
	conditionForm.seuil_credit_requis = condition.seuil_credit_requis ?? 30
	conditionForm.is_active = condition.is_active
	seuilCreditManualEdit.value = false
}

function resetConditionForm() {
	conditionForm.intitule = ''
	conditionForm.code = undefined
	conditionForm.description = undefined
	conditionForm.note_min_modifiable = undefined
	conditionForm.note_max_modifiable = undefined
	conditionForm.points_max_ajoutables = undefined
	conditionForm.nombre_max_modifications = undefined
	conditionForm.note_eliminatoire = undefined
	conditionForm.seuil_credit_requis = 30
	conditionForm.is_active = true
	seuilCreditManualEdit.value = false
}

async function saveCondition() {
	if (!conditionFormValid.value) return

	conditionSaving.value = true
	try {
		const payload = { ...conditionForm }
		// Toujours envoyer le seuil (30) pour cohérence avec le backend.
		if (!payload.code) delete payload.code

		let result
		if (editingCondition.value) {
			result = await updateDeliberationCondition(editingCondition.value.id, payload)
		} else {
			result = await createDeliberationCondition(payload)
		}

		if (result.success) {
			// Le composable ne montre plus de toast, on affiche le message ici
			toast.success(result.message || (editingCondition.value ? 'Condition modifiée avec succès' : 'Condition créée avec succès'))
			closeConditionForm()
			await loadConditions()
			await loadCreateConditions()
		} else {
			// Afficher le message d'erreur proprement extrait
			// Le backend peut retourner des messages spécifiques pour :
			// - Délibération terminée
			// - Étudiant validé en phase 2
			// - Permissions insuffisantes
			const errorMessage = result.message || 'Erreur lors de l\'opération'
			
			// Si le message suggère de créer une nouvelle condition, afficher un message plus explicite
			if (errorMessage.includes('terminée') || errorMessage.includes('terminé')) {
				toast.warning('Cette condition est utilisée dans une délibération terminée. Veuillez créer une nouvelle condition pour les prochaines délibérations.')
			} else if (errorMessage.includes('phase 2') || errorMessage.includes('validé')) {
				toast.warning('Cette condition ne peut pas être modifiée car au moins un étudiant a été validé en phase 2.')
			} else {
				toast.error(errorMessage)
			}
		}
	} finally {
		conditionSaving.value = false
	}
}

async function toggleConditionActive(condition: DeliberationCondition) {
	conditionTogglingId.value = condition.id
	try {
		const result = await toggleActiveDeliberationCondition(condition.id)
		if (result.success) {
			await loadConditions()
			await loadCreateConditions()
		}
	} finally {
		conditionTogglingId.value = null
	}
}

function confirmDeleteCondition(condition: DeliberationCondition) {
	conditionItemToDelete.value = condition
	conditionDeleteDialogOpen.value = true
}

async function deleteCondition() {
	if (!conditionItemToDelete.value) return

	conditionDeleting.value = true
	try {
		const result = await deleteDeliberationCondition(conditionItemToDelete.value.id)
		if (result.success) {
			// Le composable affiche déjà le toast, pas besoin d'en afficher un autre
			conditionDeleteDialogOpen.value = false
			conditionItemToDelete.value = null
			await loadConditions()
			await loadCreateConditions()
		} else {
			// Afficher le message d'erreur proprement extrait
			toast.error(result.message || 'Erreur lors de la suppression')
		}
	} finally {
		conditionDeleting.value = false
	}
}

// ============================================================================
// CRÉATION DÉLIBÉRATION (MODALE)
// ============================================================================
const createDeliberationDialog = ref(false)
const createForm = reactive<DeliberationFormData>({
	session_id: null,
	specialite_id: null,
	semestre_id: null,
	type_deliberation: 'premiere',
	deliberation_condition_id: null,
	responsable_id: null,
	predeliberation_responsable_id: null,
	commentaire: undefined,
	predeliberation_commentaire: undefined
})
const createFormValid = ref(false)
const createFormRef = ref()
const saving = ref(false)

const { specialites, fetchSpecialites } = useSpecialite()
const { semestres, fetchSemestres } = useSemestres()
const { personnels, personnelsForSelect, fetchPersonnels } = usePersonnelList()
const { fetchMaquettesBatch } = useMaquettes()

// Ref locale pour stocker les maquettes chargées pour le formulaire de création
const loadedMaquettes = ref<any[]>([])

const loadingSessions = ref(false)
const loadingSpecialites = ref(false)
const loadingSemestres = ref(false)
const loadingPersonnels = ref(false)
const loadingConditions = ref(false)
const loadingMaquettes = ref(false)

const selectedSession = computed(() => {
	if (!createForm.session_id) return null
	return sessions.value.find(s => s.id === createForm.session_id)
})

const {
	isClosed: createSessionClosed,
	canMutate: canMutateCreateSession,
	archiveHint: createArchiveHint,
} = useClosedSessionGuard(selectedSession)

const sessionsForSelect = computed(() => {
	const list = sessions.value.filter(s => {
		if (isSessionCloturee(s)) return false
		if (!historiqueEnabled.value && !s.is_active) return false
		return true
	})
	const sorted = sortSessionsByEtat(list)
	return sorted.map(s => ({
		title: s.nom || s.code,
		value: s.id,
		etat: getSessionEtat(s),
		etatColor: getSessionEtatColor(getSessionEtat(s)),
		cloturee: isSessionCloturee(s),
	}))
})

const specialitesForSelect = computed(() => {
	if (!createForm.session_id || loadingMaquettes.value) return []
	// Filtrer les spécialités selon les maquettes de la session
	const maquettesSession = loadedMaquettes.value.filter(m => m.session_id === createForm.session_id)
	if (maquettesSession.length === 0) return []
	
	const specialiteIds = new Set<number>()
	maquettesSession.forEach(m => {
		if (m.classe_specialite?.id_specialite) {
			specialiteIds.add(m.classe_specialite.id_specialite)
		} else if (m.classe_specialite?.specialite?.id) {
			specialiteIds.add(m.classe_specialite.specialite.id)
		}
	})
	
	if (specialiteIds.size === 0) return []
	
	return specialites.value
		.filter(s => specialiteIds.has(s.id))
		.map(s => ({
			title: s.intitule || s.code,
			value: s.id
		}))
})

const semestresForSelect = computed(() => {
	if (!createForm.session_id || !createForm.specialite_id || loadingMaquettes.value) return []
	// Filtrer les semestres selon la session et la spécialité
	const maquettesSession = loadedMaquettes.value.filter(m => {
		if (m.session_id !== createForm.session_id) return false
		const specId = m.classe_specialite?.id_specialite || m.classe_specialite?.specialite?.id
		return specId === createForm.specialite_id
	})
	if (maquettesSession.length === 0) return []
	
	const semestreIds = new Set<number>()
	maquettesSession.forEach(m => {
		if (m.ues) {
			m.ues.forEach((ue: any) => {
				if (ue.semestre?.id) {
					semestreIds.add(ue.semestre.id)
				} else if (ue.semestre_id) {
					semestreIds.add(ue.semestre_id)
				}
			})
		}
	})
	
	if (semestreIds.size === 0) return []
	
	return semestres.value
		.filter(s => semestreIds.has(s.id) && s.session_id === createForm.session_id)
		.map(s => ({
			title: `Semestre ${s.numero}`,
			value: s.id
		}))
})

const professeursForSelect = computed(() => {
	// Retourner TOUS les personnels de type "Enseignement" ou "Administration" du système
	// Pas de filtrage par maquette ou session
	if (!personnels.value || personnels.value.length === 0) {
		return []
	}
	
	const personnelsFiltered = personnels.value.filter(p => {
		const type = p.type || ''
		return type === 'Enseignement' || type === 'Administration'
	})
	
	return personnelsFiltered.map(p => ({
		title: `${p.personne.nom} ${p.personne.prenom}`,
		subtitle: p.personne.email,
		value: p.id
	}))
})

const responsablesForSelect = computed(() => {
	if (!createForm.session_id || !selectedSession.value) return []
	
	// Filtrer selon les responsables pédagogiques de la session
	const responsablePrincipalId = selectedSession.value.responsable_pedagogique_principal_id
	const responsableSuppleantId = selectedSession.value.responsable_pedagogique_suppleant_id
	
	if (!responsablePrincipalId && !responsableSuppleantId) {
		return []
	}
	
	if (personnels.value.length === 0) {
		return []
	}
	
	// Utiliser personnels.value directement pour avoir accès aux données brutes
	const responsables = personnels.value.filter(p => {
		return (responsablePrincipalId && p.id === responsablePrincipalId) || 
		       (responsableSuppleantId && p.id === responsableSuppleantId)
	})
	
	return responsables.map(p => ({
		title: `${p.personne.nom} ${p.personne.prenom}`,
		subtitle: p.personne.email,
		value: p.id
	}))
})

const createConditions = ref<any[]>([])

const conditionsForSelect = computed(() => {
	return createConditions.value
		.filter(c => c.is_active)
		.map(c => ({
			title: c.intitule || c.code,
			value: c.id
		}))
})

async function loadCreateSessions() {
	loadingSessions.value = true
	try {
		await fetchSessions({ withRelations: true, ...fetchOptions() })
	} finally {
		loadingSessions.value = false
	}
}

async function loadCreateSpecialites() {
	if (!createForm.session_id) return
	loadingSpecialites.value = true
	try {
		// Spécialités déjà chargées à l'ouverture du dialog ; ne pas rappeler loadCreateMaquettes (évite double batch) ni fetchSpecialites
		const alreadyLoaded = loadedMaquettes.value.some((m: any) => m.session_id === createForm.session_id)
		if (!alreadyLoaded) await loadCreateMaquettes()
	} finally {
		loadingSpecialites.value = false
	}
}

async function loadCreateSemestres() {
	// Semestres déjà chargés à l'ouverture du dialog ; pas de refetch
	if (!createForm.session_id || !createForm.specialite_id) return
	loadingSemestres.value = true
	try {
		// Ne pas appeler fetchSemestres() : données déjà en mémoire
	} finally {
		loadingSemestres.value = false
	}
}

async function loadCreateMaquettes() {
	if (!createForm.session_id) return
	const alreadyLoaded = loadedMaquettes.value.some((m: any) => m.session_id === createForm.session_id)
	if (alreadyLoaded) return
	loadingMaquettes.value = true
	try {
		const fetchedMaquettes = await fetchMaquettesBatch({ 
			session_id: createForm.session_id,
			with_plannings: false,
			with_seances: false,
			includeDeliberationMaquettes: true
		})
		
		// Mettre à jour les maquettes locales
		if (Array.isArray(fetchedMaquettes) && fetchedMaquettes.length > 0) {
			// Remplacer les maquettes existantes pour cette session
			loadedMaquettes.value = [
				...loadedMaquettes.value.filter((m: any) => m.session_id !== createForm.session_id),
				...fetchedMaquettes
			]
		} else {
			// Si aucune maquette trouvée, vider les maquettes pour cette session
			loadedMaquettes.value = loadedMaquettes.value.filter((m: any) => m.session_id !== createForm.session_id)
		}
	} catch (error) {
		console.error('Erreur lors du chargement des maquettes:', error)
		loadedMaquettes.value = loadedMaquettes.value.filter((m: any) => m.session_id !== createForm.session_id)
	} finally {
		loadingMaquettes.value = false
	}
}

async function loadCreateConditions() {
	loadingConditions.value = true
	try {
		const result = await fetchDeliberationConditions({ 
			is_active: true,
			per_page: 100
		})
		if (result.success) {
			createConditions.value = Array.isArray(result.data) ? result.data : result.data?.data || []
		}
	} finally {
		loadingConditions.value = false
	}
}

async function loadCreatePersonnels() {
	if (loadingPersonnels.value) {
		return
	}
	loadingPersonnels.value = true
	try {
		// Utiliser l'endpoint non paginé pour charger tous les personnels d'un coup
		const { apiDjangoAuth } = useApi()
		const response = await apiDjangoAuth('/personnels/non_paginees/') as any
		const data = response?.results || response || []
		
		// Mapper les personnels avec extraction du type depuis les groupes
		personnels.value = (Array.isArray(data) ? data : []).map((p: any) => {
			// Extraire le type depuis les groupes
			let type = p.type || ''
			if (!type || type.trim() === '') {
				const groups = p.personne?.user?.groups || p.user?.groups || []
				const groupNames = groups.map((g: any) => g.name || g).filter(Boolean)
				if (groupNames.some((name: string) => name === 'Enseignant' || name === 'Enseignement')) {
					type = 'Enseignement'
				} else if (groupNames.some((name: string) => 
					name === 'Administration' || name === 'Admin' || name === 'Administrateur' ||
					name === 'Secrétaire' || name === 'Comptable' || name === 'RH'
				)) {
					type = 'Administration'
				}
			}
			
			return {
				id: p.id,
				matricule: p.matricule || '',
				type: type,
				personne: {
					nom: p.personne?.nom || '',
					prenom: p.personne?.prenom || '',
					email: p.personne?.email || ''
				}
			}
		})
	} catch (error) {
		console.error('❌ [loadCreatePersonnels] Erreur:', error)
		// En cas d'erreur, utiliser la méthode normale
		await fetchPersonnels()
	} finally {
		loadingPersonnels.value = false
	}
}

async function onSessionChange() {
	createForm.specialite_id = null
	createForm.semestre_id = null
	createForm.predeliberation_responsable_id = null
	createForm.responsable_id = null
	if (createForm.session_id) {
		if (personnels.value.length === 0) {
			await loadCreatePersonnels()
		}
		await loadCreateMaquettes()
	}
}

async function openCreateDeliberationDialog() {
	resetCreateForm()
	createDeliberationDialog.value = true
	// Charger sessions, spécialités, semestres et conditions une seule fois (pas de refetch au changement session/spécialité)
	await Promise.all([
		loadCreateSessions(),
		loadCreateConditions(),
		loadCreatePersonnels(),
		fetchSpecialites(),
		fetchSemestres({ ...fetchOptions() })
	])
}

function closeCreateDeliberationDialog() {
	createDeliberationDialog.value = false
	setTimeout(() => {
		resetCreateForm()
		createFormRef.value?.resetValidation()
	}, 300)
}

function resetCreateForm() {
	createForm.session_id = null
	createForm.specialite_id = null
	createForm.semestre_id = null
	createForm.type_deliberation = 'premiere'
	createForm.deliberation_condition_id = null
	createForm.responsable_id = null
	createForm.predeliberation_responsable_id = null
	createForm.commentaire = undefined
	createForm.predeliberation_commentaire = undefined
	// Réinitialiser les maquettes chargées
	loadedMaquettes.value = []
}

async function createDeliberation() {
	if (!createFormValid.value) return
	if (!canMutateCreateSession.value) {
		toast.warning(createArchiveHint.value || 'Session clôturée — consultation uniquement.')
		return
	}

	saving.value = true
	try {
		// Vérifier que le seuil choisi correspond au total crédits (maquette/semestre)
		const selectedCondition = conditions.value.find(c => c.id === createForm.deliberation_condition_id) ?? null
		const expectedSeuil = selectedCondition?.seuil_credit_requis ?? 30

		const maquettesSession = loadedMaquettes.value.filter(m => {
			if (m.session_id !== createForm.session_id) return false
			const specId = m.classe_specialite?.id_specialite || m.classe_specialite?.specialite?.id
			return specId === createForm.specialite_id
		})
		if (maquettesSession.length > 0) {
			// On considère la première maquette correspondante
			const maquette = maquettesSession[0]
			const semestreId = createForm.semestre_id
			if (maquette?.ues && semestreId) {
				let totalCredits = 0
				for (const ue of maquette.ues) {
					const ueSemestreId = ue.semestre?.id || ue.semestre_id
					if (Number(ueSemestreId) === Number(semestreId)) {
						totalCredits += Number(ue.credit || 0)
					}
				}
				if (totalCredits !== Number(expectedSeuil)) {
					toast.error(`Incohérence crédits: maquette=${totalCredits} mais condition=${expectedSeuil}. Corrigez la maquette ou ajustez le seuil.`)
					return
				}
			}
		}

		const payload: DeliberationRequest = {
			session_id: createForm.session_id!,
			specialite_id: createForm.specialite_id!,
			semestre_id: createForm.semestre_id!,
			type_deliberation: createForm.type_deliberation,
			deliberation_condition_id: createForm.deliberation_condition_id!,
			responsable_id: createForm.responsable_id!,
			predeliberation_responsable_id: createForm.predeliberation_responsable_id!,
			commentaire: createForm.commentaire || undefined,
			predeliberation_commentaire: createForm.predeliberation_commentaire || undefined
		}

		const result = await createDeliberationWithPredeliberation(payload)
		if (result.success) {
			// Le composable affiche déjà le toast, pas besoin d'en afficher un autre
			closeCreateDeliberationDialog()
			await loadDeliberations()
		} else {
			// Afficher le message d'erreur proprement extrait
			toast.error(result.message || 'Erreur lors de la création')
		}
	} finally {
		saving.value = false
	}
}

// ============================================================================
// COMPOSABLES
// ============================================================================
const {
	fetchDeliberations,
	fetchDeliberationConditions,
	createDeliberationCondition,
	updateDeliberationCondition,
	deleteDeliberationCondition,
	toggleActiveDeliberationCondition,
	createDeliberationWithPredeliberation,
	updateDeliberation,
	reactiverDeliberation: apiReactiverDeliberation,
	getDeliberation,
	getRattrapageDeliberationOutcome,
	getDeliberationSnapshot,
	loading
} = useDeliberation()

const rattrapageDialog = reactive<{
	open: boolean
	loading: boolean
	error: string | null
	items: any[]
	deliberation: Deliberation | null
}>({
	open: false,
	loading: false,
	error: null,
	items: [],
	deliberation: null
})

// Watch pour charger les semestres quand la spécialité change (seulement si le formulaire est ouvert)
watch(() => createForm.specialite_id, async (newVal) => {
	if (newVal && createForm.session_id && createDeliberationDialog.value) {
		createForm.semestre_id = null
		await loadCreateSemestres()
	}
}, { immediate: false })

// Watch pour s'assurer que les personnels sont chargés quand on sélectionne une session (seulement si le formulaire est ouvert)
watch(() => createForm.session_id, async (newVal) => {
	if (newVal && createDeliberationDialog.value && personnels.value.length === 0 && !loadingPersonnels.value) {
		await loadCreatePersonnels()
	}
}, { immediate: false })

// ============================================================================
// MODAL MODIFICATION DÉLIBÉRATION
// ============================================================================
const editDeliberationDialog = ref(false)
const editingDeliberation = ref<Deliberation | null>(null)
const editForm = reactive({
	deliberation_condition_id: null as number | null,
	responsable_id: null as number | null,
	commentaire: '' as string
})
const editFormValid = ref(false)
const editFormRef = ref()
const savingEdit = ref(false)

function handleViewDeliberation(deliberation: Deliberation) {
	navigateTo(`/admin/deliberation/${deliberation.id}`)
}

async function handleViewRattrapage(deliberation: Deliberation) {
	// Ouvrir le modal de rattrapage avec les données de la délibération sélectionnée
	rattrapageDialog.open = true
	rattrapageDialog.loading = true
	rattrapageDialog.error = null
	rattrapageDialog.items = []
	rattrapageDialog.deliberation = deliberation

	try {
		const isSeconde = deliberation.type_deliberation === 'seconde'
		const useSnapshot = deliberation.etat === 'valide'

		const { items, error } = await loadRattrapageOutcomeItems({
			isSecondeDeliberation: isSeconde,
			fetchSnapshotRaw: useSnapshot
				? async () => {
						const r = await getDeliberationSnapshot(deliberation.id)
						return r.success ? (r.data?.raw as Record<string, unknown> | undefined) : null
					}
				: undefined,
			fetchLive: () => getRattrapageDeliberationOutcome(deliberation.id),
		})

		if (error) {
			rattrapageDialog.error = error
			return
		}

		const rattrapageItems = items as any[]

		// Recharger la liste des étudiants de la session/spécialité pour reconstruire les infos
		if (deliberation.session_id && deliberation.specialite_id) {
			const { results: allStudents, truncated } = await fetchAllEtudiantsBisForSessionSpecialite(
				fetchEtudiantsBisPageSilent,
				deliberation.session_id,
				deliberation.specialite_id
			)
			warnEtudiantsBisTruncated(truncated, 'délibération')

			const studentByInscriptionId = buildMapByLaravelInscriptionId(allStudents, (student) => ({
				nom_complet: `${student.personne?.nom || ''} ${student.personne?.prenom || ''}`.trim() || 'Nom non disponible',
				matricule: student.matricule || student.code || null
			}))

			// Enrichir les éléments de rattrapage avec ces infos et trier par ordre alphabétique
			for (const ec of rattrapageItems) {
				if (!Array.isArray(ec.etudiants)) continue
				for (const etu of ec.etudiants) {
					const insId = Number(etu.inscription_id)
					const info = studentByInscriptionId.get(insId)
					if (info) {
						etu.nom_complet = info.nom_complet
						etu.matricule = info.matricule
					}
				}
				// Trier les étudiants par ordre alphabétique (nom_complet)
				ec.etudiants.sort((a: any, b: any) => {
					const nomA = (a?.nom_complet || a?.matricule || '').toLowerCase().trim()
					const nomB = (b?.nom_complet || b?.matricule || '').toLowerCase().trim()
					return nomA.localeCompare(nomB, 'fr', { numeric: true, sensitivity: 'base' })
				})
			}
		}

		rattrapageDialog.items = rattrapageItems
	} finally {
		rattrapageDialog.loading = false
	}
}

function closeRattrapageDialog() {
	rattrapageDialog.open = false
}

async function handleEditDeliberation(deliberation: Deliberation) {
	if (isSessionCloturee(deliberation.session) && !canOverrideClosedSession.value) {
		toast.warning('Session clôturée — consultation uniquement.')
		return
	}
	editingDeliberation.value = deliberation
	editForm.deliberation_condition_id = deliberation.deliberation_condition_id
	editForm.responsable_id = deliberation.responsable_id
	editForm.commentaire = deliberation.commentaire || ''
	
	// Charger les données nécessaires
	if (personnels.value.length === 0) {
		await loadCreatePersonnels()
	}
	await loadCreateConditions()
	
	// Charger les maquettes de la session pour filtrer les professeurs
	if (deliberation.session_id) {
		createForm.session_id = deliberation.session_id
		await loadCreateMaquettes()
	}
	
	editDeliberationDialog.value = true
}

function closeEditDeliberationDialog() {
	editDeliberationDialog.value = false
	setTimeout(() => {
		editingDeliberation.value = null
		editForm.deliberation_condition_id = null
		editForm.responsable_id = null
		editForm.commentaire = ''
		editFormRef.value?.resetValidation()
	}, 300)
}

async function saveEditDeliberation() {
	if (!editFormValid.value || !editingDeliberation.value) return
	
	savingEdit.value = true
	try {
		const result = await updateDeliberation(editingDeliberation.value.id, {
			deliberation_condition_id: editForm.deliberation_condition_id!,
			responsable_id: editForm.responsable_id!,
			commentaire: editForm.commentaire || undefined
		})
		
		if (result.success) {
			// Le composable ne montre plus de toast, on affiche le message ici
			toast.success(result.message || 'Délibération modifiée avec succès')
			closeEditDeliberationDialog()
			await loadDeliberations()
		} else {
			// Afficher le message d'erreur proprement extrait
			toast.error(result.message || 'Erreur lors de la mise à jour')
		}
	} finally {
		savingEdit.value = false
	}
}

const reactivateDeliberationDialog = ref(false)
const deliberationToReactivate = ref<Deliberation | null>(null)

// ============================================================================
// TÉLÉCHARGEMENT DOCUMENTS
// ============================================================================
const downloadDocumentsDialog = ref(false)
const selectedDeliberationForDownload = ref<Deliberation | null>(null)

const { getResultatsClasse } = useResultatsClasse()

const resultatsClasseDialog = reactive<{
	open: boolean
	loading: boolean
	error: string | null
	session: any | null
	specialite: any | null
	variant: ImpressionResultatsVariant
}>({
	open: false,
	loading: false,
	error: null,
	session: null,
	specialite: null,
	variant: 'classe',
})

const impressionDialogTitle = computed(() => {
	switch (resultatsClasseDialog.variant) {
		case 'classe':
			return 'Résultats de classe'
		case 'classe-anonyme':
			return 'Résultats de classe anonymisé'
		case 'annuel':
			return 'Résultats annuels'
		case 'annuel-anonyme':
			return 'Résultats annuels anonymisé'
		default: {
			const _exhaustive: never = resultatsClasseDialog.variant
			return _exhaustive
		}
	}
})

const impressionDialogHint = computed(() => {
	switch (resultatsClasseDialog.variant) {
		case 'classe':
		case 'classe-anonyme':
			return 'Chargement des données et préparation du PDF'
		case 'annuel':
		case 'annuel-anonyme':
			return 'Chargement des données et préparation de l’Excel'
		default: {
			const _exhaustive: never = resultatsClasseDialog.variant
			return _exhaustive
		}
	}
})

function closeResultatsClasseDialog() {
	resultatsClasseDialog.open = false
	resultatsClasseDialog.loading = false
	resultatsClasseDialog.error = null
	resultatsClasseDialog.session = null
	resultatsClasseDialog.specialite = null
	resultatsClasseDialog.variant = 'classe'
}

async function handleDownloadResultatsClasse(payload: {
	session: any
	specialite: any
	variant: ImpressionResultatsVariant
}) {
	const { session, specialite, variant } = payload
	if (!session?.id || !specialite?.id) return

	resultatsClasseDialog.open = true
	resultatsClasseDialog.loading = true
	resultatsClasseDialog.error = null
	resultatsClasseDialog.session = session
	resultatsClasseDialog.specialite = specialite
	resultatsClasseDialog.variant = variant

	try {
		const result = await getResultatsClasse(session.id, specialite.id)
		if (!result.success || !result.data) {
			resultatsClasseDialog.error = result.message || 'Document indisponible'
			return
		}

		switch (variant) {
			case 'classe':
				await generateResultatsClassePDF(result.data)
				toast.success('Résultats de classe téléchargés')
				break
			case 'classe-anonyme':
				await generateResultatsClassePDF(result.data, { anonymise: true })
				toast.success('Résultats de classe anonymisés téléchargés')
				break
			case 'annuel':
				await generateResultatsAnnuelsExcel(result.data, {
					anonymise: false,
					fetchEtudiantsBis: fetchEtudiantsBisPageSilent,
				})
				toast.success('Résultats annuels téléchargés')
				break
			case 'annuel-anonyme':
				await generateResultatsAnnuelsExcel(result.data, {
					anonymise: true,
					fetchEtudiantsBis: fetchEtudiantsBisPageSilent,
				})
				toast.success('Résultats annuels anonymisés téléchargés')
				break
			default: {
				const _exhaustive: never = variant
				throw new Error(`Variant d’impression inconnue: ${_exhaustive}`)
			}
		}
		closeResultatsClasseDialog()
	} catch (error: any) {
		console.error('Erreur téléchargement résultats:', error)
		resultatsClasseDialog.error = error?.message || 'Erreur lors de la génération du document'
	} finally {
		resultatsClasseDialog.loading = false
	}
}

function handleDownloadDocuments(deliberation: Deliberation) {
	selectedDeliberationForDownload.value = deliberation
	downloadDocumentsDialog.value = true
}

async function handleDownloadDocumentsConfirm(payload: import('@/types/documentExport').DeliberationDocumentsDownloadPayload) {
	if (!selectedDeliberationForDownload.value) return

	const { documentIds, tableauOptions } = payload

	try {
		const { generateDeliberationDocumentsPDF } = await import('@/composables/useDeliberationPDF')
		
		for (const docId of documentIds) {
			await generateDeliberationDocumentsPDF(
				selectedDeliberationForDownload.value,
				docId,
				docId === 'tableau' ? tableauOptions : undefined
			)
			// Petit délai entre chaque téléchargement pour éviter les problèmes
			await new Promise(resolve => setTimeout(resolve, 500))
		}
		
		toast.success(`${documentIds.length} document(s) téléchargé(s) avec succès`)
	} catch (error: any) {
		console.error('Erreur lors du téléchargement:', error)
		toast.error('Erreur lors du téléchargement des documents')
	}
}

function handleReactivateDeliberation(deliberation: Deliberation) {
	if (isSessionCloturee(deliberation.session) && !canOverrideClosedSession.value) {
		toast.warning('Session clôturée — consultation uniquement.')
		return
	}
	deliberationToReactivate.value = deliberation
	reactivateDeliberationDialog.value = true
}

async function doReactivateDeliberation() {
	if (!deliberationToReactivate.value) return
	
	try {
		const result = await apiReactiverDeliberation(deliberationToReactivate.value.id)
		if (result.success) {
			reactivateDeliberationDialog.value = false
			deliberationToReactivate.value = null
			await loadDeliberations()
		}
	} catch (error: any) {
		console.error('Erreur réactivation:', error)
	}
}

// Debounce pour éviter trop de requêtes lors des changements de filtres
let loadDeliberationsTimeout: ReturnType<typeof setTimeout> | null = null

// Watch sur les filtres avec debounce
watch(() => [deliberationFilters.search, deliberationFilters.etat], () => {
	if (loadDeliberationsTimeout) clearTimeout(loadDeliberationsTimeout)
	loadDeliberationsTimeout = setTimeout(() => {
		deliberationFilters.page = 1 // Reset à la page 1
		loadDeliberations()
	}, 500)
}, { deep: true })

// ============================================================================
// INITIALISATION
// ============================================================================
onMounted(async () => {
	await loadDeliberations()
})

watch(contextKey, async () => {
	if (!historiqueEnabled.value && deliberationFilters.session_id) {
		const selected = sessions.value.find((s: any) => s.id === deliberationFilters.session_id)
		if (isSessionHistorique(selected)) {
			deliberationFilters.session_id = null
		}
	}
	await fetchSessions({ withRelations: true, ...fetchOptions() })
	await fetchSemestres({ ...fetchOptions() })
	if (deliberationFilters.session_id && isOrphanSelectId(deliberationFilters.session_id, sessions.value)) {
		deliberationFilters.session_id = null
	}
	if (deliberationFilters.semestre_id && isOrphanSelectId(deliberationFilters.semestre_id, semestres.value)) {
		deliberationFilters.semestre_id = null
	}
	await loadDeliberations()
})
</script>

<style scoped>
.v-data-table {
	border-radius: 4px;
	background: #fdfdfd;
	border: 1px solid #e0e0e0;
}

:deep(.v-data-table thead tr) {
	background-color: #417985 !important;
	color: white !important;
	font-weight: bold;
	text-transform: uppercase;
}

:deep(.v-data-table .v-data-table__wrapper table) {
	border-collapse: separate !important;
	border-spacing: 0 8px !important;
}

.v-data-table tbody tr:hover {
	background: #f5f5f5 !important;
}

.modal-header-primary {
	background-color: #417985 !important;
	color: #fff !important;
	border-radius: 3px 3px 0 0;
}
.modal-header-primary-text {
	color: #fff !important;
}
.modal-header-primary-icon .v-icon {
	color: #fff !important;
}

.session-panel-title {
	line-height: 1.35;
}

.session-panel-title :deep(.delib-session-summary) {
	padding-right: 4px;
}

:deep(.v-expansion-panel-text__wrapper) {
	padding-inline: 8px 12px;
}
</style>
